import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Sparkles, Zap } from 'lucide-react';
import { useIrisAvatar } from '../hooks/useIrisAvatar';

interface IrisAvatarProps {
  onClick?: () => void;
  showMessage?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const IrisAvatar: React.FC<IrisAvatarProps> = ({ 
  onClick, 
  showMessage = true, 
  size = 'large' 
}) => {
  const { avatarUrl, loading } = useIrisAvatar();
  const sizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-24 h-24', 
    large: 'w-32 h-32'
  };

  const containerSizeClasses = {
    small: 'w-20 h-20',
    medium: 'w-28 h-28',
    large: 'w-36 h-36'
  };

  return (
    <div className="flex items-center space-x-6">
      {/* Avatar avec animations */}
      <motion.div
        className={`relative ${containerSizeClasses[size]} cursor-pointer`}
        onClick={onClick}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {/* Cercles d'animation en arrière-plan */}
        <motion.div
          className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400/20 to-purple-400/20"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <motion.div
          className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-400/20 to-pink-400/20"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />

        {/* Particules flottantes */}
        <motion.div
          className="absolute -top-2 -right-2 w-3 h-3 bg-yellow-400 rounded-full"
          animate={{
            y: [-10, -20, -10],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <motion.div
          className="absolute -bottom-1 -left-1 w-2 h-2 bg-blue-400 rounded-full"
          animate={{
            y: [0, -15, 0],
            opacity: [0.3, 0.8, 0.3]
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        />

        {/* Image de l'avatar */}
        <motion.div
          className={`relative ${sizeClasses[size]} rounded-full overflow-hidden border-4 border-white shadow-2xl bg-gradient-to-br from-blue-50 to-purple-50`}
          animate={{
            boxShadow: [
              '0 0 20px rgba(59, 130, 246, 0.3)',
              '0 0 30px rgba(147, 51, 234, 0.4)',
              '0 0 20px rgba(59, 130, 246, 0.3)'
            ]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <img 
            src={avatarUrl} 
            alt="Iris IA Assistant" 
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback vers l'image par défaut en cas d'erreur
              (e.target as HTMLImageElement).src = '/irisavatar.jpg';
            }}
          />
          
          {/* Overlay avec effet de brillance */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent"
            animate={{
              opacity: [0, 0.3, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.div>

        {/* Indicateur d'activité */}
        <motion.div
          className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center"
          animate={{
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <Zap className="w-3 h-3 text-white" />
        </motion.div>

        {/* Icônes flottantes */}
        <motion.div
          className="absolute -top-3 left-1/2 transform -translate-x-1/2"
          animate={{
            y: [-5, -15, -5],
            rotate: [0, 10, 0]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <Sparkles className="w-4 h-4 text-yellow-400" />
        </motion.div>
      </motion.div>

      {/* Message marketing */}
      {showMessage && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex-1"
        >
          <motion.div
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20 relative"
            whileHover={{ scale: 1.02 }}
          >
            {/* Flèche pointant vers l'avatar */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-2">
              <div className="w-4 h-4 bg-white/90 rotate-45 border-l border-b border-white/20"></div>
            </div>
            
            <div className="flex items-start space-x-3">
              <MessageCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  💬 Discutez avec Iris, votre assistante IA
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Obtenez des réponses instantanées sur nos services, demandez un devis 
                  ou planifiez une consultation. Iris est là 24/7 pour vous accompagner !
                </p>
                <motion.button
                  onClick={onClick}
                  className="mt-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-2 rounded-lg font-medium hover:from-amber-600 hover:to-amber-700 transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Commencer la conversation</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default IrisAvatar;
